import React from 'react';
import { Info, DollarSign, Calculator, PiggyBank, Zap, AlertTriangle, Globe, TrendingUp, Shield } from 'lucide-react';

/**
 * Компонент с информацией о валютной ипотеке и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о валютной ипотеке: особенности, риски, преимущества
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-500" />
          Что такое валютная ипотека?
        </h3>
        <p>
          <strong>Валютная ипотека</strong> — это ипотечный кредит в иностранной валюте (доллары США, евро и др.), который выдается для приобретения недвижимости. Основное отличие от рублевой ипотеки заключается в валюте кредита и, соответственно, в валюте платежей по нему. Хотя физически вы можете вносить платежи в рублях, размер платежа будет зависеть от текущего обменного курса.
        </p>

        <h3 className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-indigo-500" />
          Основные преимущества валютной ипотеки
        </h3>
        <ul>
          <li><strong>Более низкие процентные ставки:</strong> Ставки по валютной ипотеке обычно значительно ниже, чем по рублевой. В то время как рублевые ставки могут составлять 7-12%, ставки по валютной ипотеке часто находятся в диапазоне 3-6%.</li>
          <li><strong>Выгода при укреплении рубля:</strong> Если рубль укрепляется относительно валюты кредита, размер платежа в рублевом эквиваленте уменьшается.</li>
          <li><strong>Защита от рублевой инфляции:</strong> Валютная ипотека может защитить от инфляции рубля, особенно в периоды экономической нестабильности.</li>
          <li><strong>Подходит для тех, кто получает доход в валюте:</strong> Идеальна для граждан, получающих доход в той же валюте, в которой выдан кредит.</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Для кого подходит валютная ипотека?</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Валютная ипотека наиболее безопасна для заемщиков, у которых есть стабильный доход в той же валюте, в которой оформлен кредит. Также она может быть вариантом для тех, кто уверен в укреплении рубля или стабильности валютного курса на долгосрочную перспективу.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Основные риски валютной ипотеки
        </h3>
        <p>
          Главный и существенный риск валютной ипотеки — <strong>валютный риск</strong>, то есть риск неблагоприятного изменения курса валют. История знает примеры, когда из-за резкого ослабления рубля платежи по валютным ипотекам выросли в 2-3 раза в рублевом эквиваленте.
        </p>
        <ul>
          <li><strong>Валютный риск:</strong> При ослаблении рубля размер платежа в рублевом эквиваленте увеличивается.</li>
          <li><strong>Волатильность платежей:</strong> Размер платежа в рублевом выражении может значительно колебаться из месяца в месяц.</li>
          <li><strong>Долгосрочный риск:</strong> Предсказать динамику валютных курсов на 10-20 лет (типичный срок ипотеки) практически невозможно.</li>
          <li><strong>Отсутствие государственной поддержки:</strong> Большинство государственных программ поддержки ипотечного кредитования распространяются только на рублевые кредиты.</li>
        </ul>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 my-6">
          <h4 className="text-amber-700 dark:text-amber-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Исторический опыт: кризис валютных заемщиков
          </h4>
          <p className="text-amber-600 dark:text-amber-400 text-sm mb-0">
            В 2014-2015 годах, когда курс доллара вырос примерно с 30 до 80 рублей за доллар, многие заемщики не смогли справиться с возросшей в 2-2,5 раза нагрузкой по валютным ипотечным кредитам. Это привело к массовым просрочкам, судебным искам и социальному напряжению. Впоследствии некоторым заемщикам удалось договориться с банками о реструктуризации или конвертации долга в рубли.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Как правильно оценить валютные риски
        </h3>
        <p>
          Оценка валютных рисков — ключевой этап при принятии решения о валютной ипотеке. Вот несколько важных методик оценки:
        </p>
        <ul>
          <li><strong>Стресс-тестирование бюджета:</strong> Рассчитайте, сможете ли вы обслуживать кредит, если платеж вырастет на 30%, 50% или даже 100% из-за изменения курса.</li>
          <li><strong>Анализ долговой нагрузки (DTI):</strong> Отношение ежемесячного платежа к доходу не должно превышать 30-40% даже при неблагоприятных сценариях валютного курса.</li>
          <li><strong>Оценка валютной структуры доходов и расходов:</strong> Проанализируйте, насколько ваши доходы защищены от валютных колебаний, есть ли у вас источник дохода в валюте кредита.</li>
          <li><strong>Изучение долгосрочных прогнозов:</strong> Хотя точно предсказать курс валют невозможно, изучите основные макроэкономические прогнозы и перспективы страны.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-500" />
          Стратегии защиты от валютных рисков
        </h3>
        <p>
          Существуют различные методы снижения валютных рисков при ипотечном кредитовании:
        </p>
        <ol>
          <li><strong>Формирование валютного резерва:</strong> Создайте финансовую "подушку безопасности" в валюте кредита, которая покроет платежи на 6-12 месяцев.</li>
          <li><strong>Увеличение досрочных погашений:</strong> В периоды укрепления рубля увеличивайте сумму досрочных погашений, чтобы уменьшить основной долг и будущие платежи.</li>
          <li><strong>Хеджирование валютных рисков:</strong> В некоторых случаях можно использовать финансовые инструменты (фьючерсы, опционы) для защиты от валютных колебаний.</li>
          <li><strong>Диверсификация инвестиций:</strong> Распределяйте свои инвестиции между различными валютами и активами для снижения общего риска.</li>
          <li><strong>Согласование валютной структуры доходов и обязательств:</strong> Старайтесь, чтобы валюта вашего дохода соответствовала валюте кредита.</li>
        </ol>

        <h3 className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-blue-500" />
          Популярные валюты для ипотечных кредитов в России
        </h3>
        <p>
          В разные периоды популярность разных валют для ипотеки в России менялась:
        </p>
        <ul>
          <li><strong>Доллар США (USD):</strong> Традиционно самая популярная валюта для валютных ипотек в России из-за стабильности и распространенности доллара.</li>
          <li><strong>Евро (EUR):</strong> Вторая по популярности валюта, особенно привлекательная в периоды укрепления евро к доллару.</li>
          <li><strong>Швейцарский франк (CHF):</strong> Был популярен из-за низких процентных ставок и репутации стабильной валюты, однако после резкого укрепления франка в 2015 году его популярность снизилась.</li>
          <li><strong>Китайский юань (CNY):</strong> В последние годы приобретает популярность в связи с развитием российско-китайских экономических отношений.</li>
          <li><strong>Японская иена (JPY):</strong> Привлекательна из-за традиционно низких процентных ставок, однако малораспространена на российском рынке.</li>
        </ul>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 my-6">
          <h4 className="text-green-700 dark:text-green-300 font-medium">Современная ситуация в России</h4>
          <p className="text-green-600 dark:text-green-400 text-sm mb-0">
            После кризиса валютных заемщиков 2014-2015 годов объем выдачи валютных ипотечных кредитов в России существенно сократился. Многие крупные банки либо полностью прекратили выдачу валютной ипотеки, либо существенно ужесточили требования к заемщикам. По данным на 2022-2023 годы, доля валютных ипотечных кредитов не превышает 0,5% от общего объема ипотечного кредитования в России.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          Сравнение валютной и рублевой ипотеки в России
        </h3>
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              <th className="border border-gray-300 dark:border-gray-700 p-2 text-left">Параметр</th>
              <th className="border border-gray-300 dark:border-gray-700 p-2 text-left">Валютная ипотека</th>
              <th className="border border-gray-300 dark:border-gray-700 p-2 text-left">Рублевая ипотека</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Процентная ставка</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2">3-6%</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2">7-12%</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Стабильность платежа</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Низкая (зависит от курса)</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Высокая (фиксированный платеж)</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Валютный риск</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Высокий</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Отсутствует</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Доступность госпрограмм</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Очень ограниченная</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Широкая (льготная ипотека и др.)</td>
            </tr>
            <tr>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Требования к заемщику</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Высокие (часто требуется доход в валюте)</td>
              <td className="border border-gray-300 dark:border-gray-700 p-2">Стандартные</td>
            </tr>
          </tbody>
        </table>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-green-500" />
          Рекомендации по погашению валютной ипотеки
        </h3>
        <p>
          Если вы уже взяли валютную ипотеку, вот несколько рекомендаций по ее обслуживанию:
        </p>
        <ul>
          <li><strong>Мониторинг валютного курса:</strong> Регулярно отслеживайте динамику курса валюты кредита к рублю.</li>
          <li><strong>Увеличение досрочных погашений:</strong> В периоды укрепления рубля максимально увеличивайте сумму досрочных погашений.</li>
          <li><strong>Реструктуризация при необходимости:</strong> Если возникли трудности с платежами из-за валютных колебаний, незамедлительно обращайтесь в банк для обсуждения возможности реструктуризации.</li>
          <li><strong>Рассмотрение вариантов рефинансирования:</strong> При благоприятных условиях рассмотрите возможность рефинансирования валютного кредита в рублевый.</li>
          <li><strong>Создание валютного резерва:</strong> Формируйте резерв средств в валюте кредита для защиты от неблагоприятных колебаний курса.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш калькулятор валютной ипотеки поможет вам оценить все риски и преимущества, спланировать бюджет и выбрать оптимальные условия для вашей финансовой ситуации!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;